// Copyright (c) Docugami, Inc. All rights reserved.

import { IOverflowSetItemProps, IconButton, OverflowSet, Shimmer, TooltipHost } from '@fluentui/react';
import { CardRichTextContent } from 'components/cards/CardRichTextContent';
import { BindingEvaluationRichTextModal } from 'components/cards/ExpandedRichTextModal';
import { ConfirmationButton } from 'components/common/ConfirmationButton';
import { primaryFontExpanded } from 'components/common/Fonts';
import { DgIconButton } from 'components/shell/utils/DgIconButton';
import { useDiligenceFlaggingEnabled } from 'components/work_path/WorkPathsHelper';
import { useBindingProviderDebugColor } from 'hooks/ChunkSelectionColor';
import { useFlagBinding } from 'hooks/FlagBinding';
import { dgIconNames, useDgIcons } from 'hooks/dgIcons';
import { useAppSelector } from 'hooks/redux';
import { useAddBinding, useRemoveBinding } from 'hooks/reports';
import { BindingProviderId, ReportBinding, ReportBindingEvaluationPreview, ReportEntry, ReportEntryType, ReportViewContext } from 'networking/reportsApi';
import { useMemo, useState } from 'react';
import { getBindingEvaluationKeyForBinding } from 'store/reports/reportsSlice';
import { BindingAndEvaluation, bindingAndEvaluationComparer, createSelectIsTrainingActiveForSpan, selectClusterId, selectReportContext, selectReportEntryBindings, selectReportEntryBindingsLoading, selectReportEvaluations, selectReportId, selectReportLabels } from 'store/reports/selectors';
import styled, { css } from 'styled-components';
import { AutomationId } from 'utils/Automation';
import { hasDiligenceFlag } from 'utils/DiligenceReportUtils';
import { appContentColor, emptyGray, humanBlue, machineGreen, primaryTheme } from '../shell/presentation/AppStyles';
import { RepeatCellHeader } from './RepeatHeaders';
import { ToggleCommandBar, ToggleType } from './ToggleCommandBar';
import { useQualityControlWarning } from 'hooks/qualityControlWarning';
import { folderIdSelector } from 'store/dgml/dgmlSelectors';

const CardContainer = styled.div.attrs({
    'data-componentname': 'CardContainer'
})`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
`;

const CardContentContainer = styled.div.attrs({
    'data-componentname': 'CardContentContainer'
})`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    gap: 2px;
    overflow-y: scroll;
    padding: 0px 2px 0px 10px;

    &::-webkit-scrollbar {
        width: 8px;
        background: transparent;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: transparent;
        border-radius: 4px;
    }

    &:hover::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        border: 2px solid transparent;
        background-clip: content-box;
    }

    &:hover::-webkit-scrollbar-thumb:hover {
        background-color: rgba(0, 0, 0, 0.3);
    }

    /* Firefox */
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;

    &:hover {
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    }

    /* IE and Edge support */
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

const ValueTextCss = css`
    font-family: ${primaryFontExpanded};
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    color: ${primaryTheme.semanticColors.bodyText};
`;

const ContentContainer = styled.div.attrs({
    'data-componentname': 'ContentContainer'
})`
    display: flex;
    gap: 5px;
    flex: 1;
    max-height: 200px;
    :hover {
        overflow-y: auto;
    }
    overflow: hidden;
    pointer-events: auto;
    flex-direction: column;
    position: relative;
`;

const WarningContainer = styled.div.attrs({
    'data-componentname': 'WarningContainer'
})`
    width: 15px;
    height: 100%;
    background: #FEC705;
    align-items: center;
    display: flex;
    justify-content: center;
`;

const ValueAndFlagsContainer = styled.div.attrs({
    'data-componentname': 'OuterCardContentContainer'
}) <{ backgroundColor?: string }>`
    flex: 1;
    min-height: 72px;
    max-height: 245px;
    white-space: normal;
    overflow: hidden;
    border: 1px solid #E5E5E5;
    border-radius: 5px;
    box-sizing: border-box;
    padding: 0px;
    cursor: pointer;
    display: flex;
    span {
        ${ValueTextCss};
    }
    background-color: ${props => props.backgroundColor ?? primaryTheme.palette.white};
`;

const ValueContainer = styled.div.attrs({
    'data-componentname': 'CardContentContainer'
})`
    padding: 5px;
    display: flex;
    overflow: hidden;
    width: 100%;
`;


const MenuItems = styled.div.attrs({
    'data-componentname': 'MenuItems'
})`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 6px;
    width: 18px;
`;

const EmptyText = styled.span.attrs({
    'data-componentname': 'EmptyText'
})`
    color: ${emptyGray};
    font-style: italic;
    font-weight: 600;
    opacity: .3;
`

export interface ReportTableCellProps {
    folderId: string;
    entry: ReportEntry;
    fileName: string;
    onClick?: (instanceId?: string) => void;
    disabled?: boolean;
}

export const ReportTableCell = (props: ReportTableCellProps): JSX.Element => {
    const { entry, folderId, onClick = () => { } } = props;
    const { fileId } = folderIdSelector(folderId)!
    const reportId = useAppSelector(selectReportId);
    const cellBindings = useAppSelector(state => selectReportEntryBindings(state, fileId)?.entities[entry.id]);
    const evaluations = useAppSelector(state => selectReportEvaluations(state, fileId));
    const isLoading = useAppSelector(state => selectReportEntryBindingsLoading(state, fileId));

    const { trigger: triggerAddBinding } = useAddBinding(reportId);

    const confirm = async (instanceId?: string) => {
        await triggerAddBinding({
            entryId: props.entry.id,
            folderId: folderId,
            instanceId: instanceId
        })
    }

    function getCardValue() {
        if (!!cellBindings?.bindings.length) {
            return (
                <>
                    {
                        cellBindings.bindings
                            .map<BindingAndEvaluation>(binding => {
                                const evalKey = getBindingEvaluationKeyForBinding(binding)
                                const evaluation = evaluations?.entities[evalKey];
                                return {
                                    binding: binding,
                                    key: evalKey,
                                    evaluation: evaluation
                                };
                            })
                            .sort(bindingAndEvaluationComparer)
                            .map(x => ((
                                <ReportEvaluationCell
                                    entryId={entry.id}
                                    folderId={folderId}
                                    key={`repeateval_${x.key}`}
                                    entryType={entry.entryType}
                                    binding={x.binding}
                                    evaluation={x.evaluation}
                                    label={entry.title}
                                    onClick={onClick}
                                    onConfirm={confirm}
                                    disabled={props.disabled}
                                    isLoading={isLoading} />))
                            )
                    }
                </>
            )
        }
        else {
            return (
                <ReportEvaluationCell
                    entryId={entry.id}
                    folderId={folderId}
                    key={`${entry.id}_${folderId}_empty`}
                    entryType={entry.entryType}
                    binding={undefined}
                    evaluation={undefined}
                    label={entry.title}
                    onClick={onClick}
                    onConfirm={confirm}
                    disabled={props.disabled}
                    isLoading={isLoading} />);
        }
    }

    function getHeader() {
        if (!!cellBindings?.bindings?.length) {
            return (
                <RepeatCellHeader
                    entryId={entry.id}
                    folderId={folderId}
                    count={cellBindings.bindings.filter(binding => !!binding.spanId).length ?? 0} />
            )
        } else {
            return <></>
        }
    }

    return (
        <CardContainer>
            <CardContentContainer key={entry.id} >
                {getHeader()}
                {getCardValue()}
            </CardContentContainer>
        </CardContainer>
    )
}

interface EvaluationCellProps {
    entryId: string;
    folderId: string;
    entryType: ReportEntryType;
    binding?: ReportBinding,
    evaluation?: ReportBindingEvaluationPreview;
    label: string;
    onConfirm: (instanceId?: string) => Promise<void>;
    onClick?: (instanceId?: string) => void;
    disabled?: boolean;
    isLoading?: boolean
}

export const ReportEvaluationCell = (props: EvaluationCellProps) => {
    const reportId = useAppSelector(selectReportId);
    const context = useAppSelector(selectReportContext);
    const clusterId = useAppSelector(selectClusterId);
    const reportLabels = useAppSelector(selectReportLabels);

    const { fileId } = folderIdSelector(props.folderId)!;

    const { trigger: triggerRemoveBinding, data: removeBindingData } = useRemoveBinding(reportId);
    const { binding, evaluation, entryType, entryId, folderId, label, onClick, onConfirm } = props;
    const warning = useQualityControlWarning(label, binding?.spanId);
    const isFlagged = hasDiligenceFlag(binding?.metadata);
    const flag = useFlagBinding();
    const isFlaggingEnabled = useDiligenceFlaggingEnabled();
    const [isRichTextModelOpen, setIsRichTextModalOpen] = useState(false);

    const [showHiddenMenuItems, setShowHiddenMenuItems] = useState(false);

    const isTrainingSelector = useMemo(() => createSelectIsTrainingActiveForSpan(), []);
    const isTraining = useAppSelector(state => isTrainingSelector(state, clusterId, fileId, props.binding?.spanId));

    const color = useBindingProviderDebugColor(props.binding?.bindingProviderId);
    const { icon } = useDgIcons();

    const overflowItems = useMemo<IOverflowSetItemProps[]>(() => [
        {
            key: 'clear',
            name: 'Clear',
            onClick: () => triggerRemoveBinding({
                folderId: folderId,
                entryId: props.entryId,
                instanceId: binding!.spanId!
            }),
            isVisible: true,
            disabled: !binding?.spanId || removeBindingData.isLoading,
            title: `Remove this value from your ${reportLabels.titleName.toLocaleLowerCase()}`
        }
    ], [binding, folderId, props.entryId, removeBindingData.isLoading, reportLabels.titleName, triggerRemoveBinding]);

    function getTooltip() {
        if (binding?.userConfirmed === true) {
            return isTraining ? 'Learning from your feedback for this item.' : 'Confirmed'
        }
        else {
            return 'Confirm?'
        }
    }

    function onRenderOverflowButton(overflowItems: IOverflowSetItemProps[] | undefined): JSX.Element {
        return (
            <IconButton
                iconProps={{ iconName: "MoreVertical" }}
                styles={{
                    root: {
                        "i": {
                            fontWeight: 700
                        },
                        width: "24px",
                        padding: "0px 1px",
                    },
                    icon: {
                        fontWeight: 700,
                        color: appContentColor
                    },
                }}
                menuProps={{
                    items: overflowItems?.filter(item => !!item.isVisible) || [],
                    styles: { root: { backgroundColor: primaryTheme.palette.white } }
                }}
                onRenderMenuIcon={() => null} />
        )
    }

    function onMouseOver() {
        if (props.disabled) {
            return;
        }
        setShowHiddenMenuItems(true)
    }

    function onMouseLeave() {
        if (props.disabled) {
            return;
        }
        setShowHiddenMenuItems(false)
    }

    return (
        <ValueAndFlagsContainer
            backgroundColor={color}
            onMouseOver={onMouseOver}
            onMouseLeave={onMouseLeave}
        >
            {isFlaggingEnabled && <ToggleCommandBar
                toggles={[{
                    command: {
                        execute: () => flag(reportId, entryId, folderId, evaluation?.spanId, binding?.metadata),
                        canExecute: () => true
                    },
                    isToggled: isFlagged,
                    type: ToggleType.Flag
                }]} />
            }
            {!isFlaggingEnabled && !!warning && !binding?.isConfirmed && <TooltipHost content={warning.reasoning}>
                <WarningContainer>
                    { icon(
                        dgIconNames.DgBang,
                        'Warning',
                        24,
                        9,
                        primaryTheme.palette.white,
                        '') }
                </WarningContainer>
            </TooltipHost>
            }
            <ValueContainer>
                <ReportBindingEvaluationVisual
                    entryType={entryType}
                    binding={binding}
                    bindingEvaluation={evaluation}
                    onClick={() => onClick?.(evaluation?.spanId)}
                    label={label} />
                <MenuItems>
                    <div hidden={!showHiddenMenuItems}>
                        <TooltipHost content={'Expand'}>
                            < DgIconButton
                                iconName={dgIconNames.ExpandCard}
                                height={12}
                                width={12}
                                color={appContentColor}
                                onClick={() => setIsRichTextModalOpen(true)} />
                        </TooltipHost>
                    </div>
                    <ConfirmationButton
                        isConfirmed={binding?.userConfirmed === true}
                        onClick={!binding?.userConfirmed ? () => onConfirm(evaluation?.spanId) : undefined}
                        confirmedBackgroundColor={binding?.bindingProviderId === BindingProviderId.User ? humanBlue : machineGreen}
                        tooltip={getTooltip()}
                        hidden={context !== ReportViewContext.Review}
                        data-automation-id={binding?.userConfirmed === true ? AutomationId.WorkPathConfirmedItemButton : AutomationId.WorkPathUnconfirmedItemButton}
                        pulse={isTraining} />
                    <div hidden={!showHiddenMenuItems}>
                        <OverflowSet
                            styles={{
                                root: {
                                    height: 16,
                                },
                                overflowButton: {
                                    height: 16,
                                    'button': {
                                        height: 16
                                    },
                                }

                            }}
                            overflowItems={showHiddenMenuItems ? overflowItems : []}
                            onRenderOverflowButton={onRenderOverflowButton}
                            onRenderItem={(item: IOverflowSetItemProps) => { }} />
                    </div>
                </MenuItems>
            </ValueContainer>
            <BindingEvaluationRichTextModal
                title={props.label}
                evaluation={evaluation}
                onDismiss={() => setIsRichTextModalOpen(false)}
                isOpen={isRichTextModelOpen} />
        </ValueAndFlagsContainer>
    )
}

interface ReportBindingEvaluationVisualProps {
    entryType: ReportEntryType;
    binding?: ReportBinding;
    bindingEvaluation?: ReportBindingEvaluationPreview;
    label?: string;
    isLoading?: boolean;
    onClick: () => void;
}

export const ReportBindingEvaluationVisual = (props: ReportBindingEvaluationVisualProps) => {
    const { bindingEvaluation, binding } = props;
    const isLoading = (!!binding?.spanId && !bindingEvaluation) || props.isLoading;
    const isReady = !!binding && (!binding.spanId || !!bindingEvaluation);

    if (isLoading) {
        return (
            <ContentContainer onClick={props.onClick}>
                <Shimmer width={225} />
                <Shimmer width={150} />
            </ContentContainer>
        );
    }
    else if (isReady) {
        if (!binding.spanId) {
            return <ContentContainer onClick={props.onClick} />
        }
        else {
            return (
                <ContentContainer
                    onClick={props.onClick}>
                    <CardRichTextContent text={props.bindingEvaluation?.richTextValue ?? ''} maxLines={10} />
                </ContentContainer>
            )
        }
    }
    else {
        return (
            <ContentContainer onClick={props.onClick}>
                <EmptyText>Empty</EmptyText>
            </ContentContainer>
        )
    }
}
